import { FtueVideo } from '../../api/core'
import { toFtueGameVersions } from '../features/ftue-videos/helpers/ftueVideoHelpers'
import { FTUE_VIDEO_TYPE, FtueGameVersion, FtueVideoAttachBody, FtueVideoUpdateBody } from '../features/ftue-videos/types/InternalFtueVideo'
import { InternalTag, InternalTagType } from '../types/InternalTag'
import { jsonAnswer, optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

const ALL_VIDEOS = 'LIST'
const gameVideos = (gameId: string) => `game-${gameId}`

const ftueVideosApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalFtueVideos'] }).injectEndpoints({
  endpoints: (builder) => ({
    getFtueVideos: builder.query<FtueVideo[], void>({
      query: () => ({ url: 'ftue-videos/', params: { type: FTUE_VIDEO_TYPE } }),
      providesTags: [{ type: 'internalFtueVideos', id: ALL_VIDEOS }],
    }),
    // Also lists attached videos that haven't been saved yet (`type: null`), which customers don't see.
    getFtueVideosByGame: builder.query<FtueVideo[], string>({
      query: (gameId) => `ftue-videos/game/${gameId}`,
      providesTags: (_result, _error, gameId) => [{ type: 'internalFtueVideos', id: gameVideos(gameId) }],
    }),
    getFtueVideo: builder.query<FtueVideo | null, string>({
      // A deleted id answers 200 with an empty body.
      query: (id) => ({ url: `ftue-videos/${id}`, responseHandler: optionalJson }),
      providesTags: (_result, _error, id) => [{ type: 'internalFtueVideos', id }],
    }),
    getFtueChapterTags: builder.query<InternalTag[], void>({
      query: () => ({ url: 'i/tags/list', params: { type: InternalTagType.Chapter } }),
      providesTags: [{ type: 'internalTags', id: InternalTagType.Chapter }],
    }),
    getFtueGameVersions: builder.query<FtueGameVersion[], { appId: number; marketIso: string }>({
      query: ({ appId, marketIso }) => ({ url: `versions/${appId}/list`, params: { marketIso } }),
      transformResponse: (versions: FtueGameVersion[] | null) => toFtueGameVersions(versions ?? []),
    }),
    attachFtueVideo: builder.mutation<FtueVideo, FtueVideoAttachBody>({
      query: (body) => ({ url: 'i/ftue-videos', method: 'POST', body, headers: jsonAnswer }),
      // Only on success: after a failure the page refetches the game's videos to check whether the video was stored
      // anyway, and a refetch started by invalidation would answer that with the list from before.
      invalidatesTags: (result, _error, body) =>
        result
          ? [
              { type: 'internalFtueVideos', id: ALL_VIDEOS },
              { type: 'internalFtueVideos', id: gameVideos(body.gameId) },
            ]
          : [],
    }),
    updateFtueVideo: builder.mutation<FtueVideo, FtueVideoUpdateBody>({
      query: (body) => ({ url: 'i/ftue-videos', method: 'PUT', body, headers: jsonAnswer }),
      invalidatesTags: (result, _error, body) =>
        result
          ? [
              { type: 'internalFtueVideos', id: ALL_VIDEOS },
              { type: 'internalFtueVideos', id: gameVideos(body.gameId) },
              { type: 'internalFtueVideos', id: body.id },
            ]
          : [],
    }),
    deleteFtueVideo: builder.mutation<string, Pick<FtueVideo, 'id' | 'gameId'>>({
      query: ({ id }) => ({ url: `i/ftue-videos/${id}`, method: 'DELETE', responseHandler: 'text' }),
      // Not the lists: the page refetches the one it shows to confirm the delete, and a refetch started by invalidation
      // makes that refetch resolve right away with the list from before. The page calls `invalidateFtueVideoLists` after.
      invalidatesTags: (_result, _error, { id }) => [{ type: 'internalFtueVideos', id }],
    }),
  }),
})

/** Refreshes the video lists other than the one a page has just read back. */
export const invalidateFtueVideoLists = ({ all, gameId }: { all?: boolean; gameId?: string }) =>
  ftueVideosApi.util.invalidateTags([
    ...(all ? [{ type: 'internalFtueVideos' as const, id: ALL_VIDEOS }] : []),
    ...(gameId ? [{ type: 'internalFtueVideos' as const, id: gameVideos(gameId) }] : []),
  ])

export const {
  useGetFtueVideosQuery,
  useGetFtueVideosByGameQuery,
  useGetFtueVideoQuery,
  useGetFtueChapterTagsQuery,
  useGetFtueGameVersionsQuery,
  useAttachFtueVideoMutation,
  useUpdateFtueVideoMutation,
  useDeleteFtueVideoMutation,
} = ftueVideosApi
